import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Bomb,
  ShieldAlert,
  GraduationCap,
  Building2,
  ArrowRight,
  ArrowUpRight,
  Phone,
  Clock,
  MapPin,
  ShieldCheck,
  Siren,
  Radar,
  FileSearch,
} from "lucide-react";
import { useSiteContent } from "../hooks/useSiteContent";
import { MaskedLine, FadeIn, Reveal, Eyebrow, AccentLast } from "../components/Reveal";
import { Marquee } from "../components/SiteChrome";

const HERO_IMG = "https://images.unsplash.com/photo-1632773054748-5dd3006ae91f?q=80&w=2000&auto=format&fit=crop";
const SERVICES_IMG = "https://images.unsplash.com/photo-1760228604788-db8a36d5c1a3?q=80&w=1600&auto=format&fit=crop";
const BAND_IMG = "https://images.unsplash.com/photo-1653592956557-48ae49fc5ef5?q=80&w=2000&auto=format&fit=crop";

const services = [
  {
    icon: Bomb,
    title: "Explosives & Narcotics Sweeps",
    desc: "Discreet, methodical sweeps of vehicles, buildings and grounds before your people arrive. Every find documented, every sweep reported.",
    span: "md:col-span-2",
    image: SERVICES_IMG,
  },
  {
    icon: ShieldAlert,
    title: "Knife & Weapon Detection",
    desc: "Passive screening teams that keep blades out of your venue without a single pat-down.",
  },
  {
    icon: GraduationCap,
    title: "School & Venue Inspections",
    desc: "Friendly, non-intrusive searches designed for educational environments and safeguarding policies.",
  },
  {
    icon: Building2,
    title: "Corporate & Event Protection",
    desc: "A visible deterrent with invisible operations. K9 teams for offices, festivals, stadiums and private events.",
    span: "md:col-span-2",
  },
];

const whyRows = [
  { n: "01", title: "Speed", desc: "A single detection dog can clear an area in a fraction of the time of a manual search team." },
  { n: "02", title: "Accuracy", desc: "A dog's nose is thousands of times more sensitive than any handheld device - nothing gets missed." },
  { n: "03", title: "Non-Intrusive", desc: "No queues, no physical contact, no disruption. Your guests barely notice us working." },
  { n: "04", title: "Compliance", desc: "Fully accredited teams working to British Standards, with a formal report after every deployment." },
];

const process = [
  { icon: Phone, title: "Rapid Assessment", desc: "Tell us your venue, dates and risk profile. We advise within the hour." },
  { icon: Siren, title: "Handler Dispatch", desc: "The nearest accredited K9 team mobilises with full kit and documentation." },
  { icon: Radar, title: "Methodical Sweep", desc: "A systematic search of entry points, buildings and vehicles, coordinated with your security." },
  { icon: FileSearch, title: "Formal Sweep Report", desc: "A written record of the sweep for your files, insurers and licensing." },
];

const ParallaxBand = () => {
  const content = useSiteContent();
  const band = content?.band || {};
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative h-[380px] sm:h-[460px] overflow-hidden grain" data-testid="parallax-band">
      <motion.img
        src={band.image || BAND_IMG}
        alt="Security team at a crowded event"
        style={{ y }}
        className="absolute inset-0 h-[124%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-brand-obsidian/70" />
      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <Reveal>
          <p className="font-display font-bold text-2xl sm:text-4xl lg:text-5xl text-white max-w-3xl leading-snug">
            {band.quote || '"One dog, one sweep, complete confidence."'}
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-4 font-eyebrow text-xs uppercase tracking-[0.3em] text-white/60">Trusted • Trained • Fully Accredited</p>
        </Reveal>
      </div>
    </section>
  );
};

function HeroSection({ content }) {
  const hero = content?.hero || {};
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);

  return (
      <section className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero-section">
        <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
          <img src={HERO_IMG} alt="Detection dog and handler on a night deployment" className="h-full w-full object-cover" />
          <div className="absolute inset-0 hero-vignette" />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 pt-28 pb-20 grid lg:grid-cols-[1.5fr_1fr] gap-12 items-center w-full">
          <div>
            <FadeIn delay={0.1}>
              <Eyebrow className="flex items-center gap-3">
                <span className="h-px w-10 bg-brand-crimson inline-block" />
                {hero.eyebrow || "UK-Wide Accredited K9 Security"}
              </Eyebrow>
            </FadeIn>
            <h1 className="mt-6 font-display font-extrabold uppercase leading-[1.02] text-4xl sm:text-6xl lg:text-7xl text-white tracking-tight">
              <MaskedLine delay={0.25}>{hero.line1 || "Detection Dogs"}</MaskedLine>
              <MaskedLine delay={0.45}>
                <AccentLast text={hero.line2 || "That Mean Business"} />
              </MaskedLine>
            </h1>
            <FadeIn delay={0.9} className="mt-6 max-w-xl text-slate-300 text-base sm:text-lg leading-relaxed">
              {hero.subtitle || ""}
            </FadeIn>
            <FadeIn delay={1.05} className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/contact-us"
                data-testid="hero-cta-contact"
                className="group inline-flex items-center gap-2 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-xs uppercase tracking-[0.2em] font-bold px-7 py-4 rounded-md transition-all duration-300 hover:shadow-[0_0_32px_rgba(200,16,46,0.55)]"
              >
                {content?.chrome?.hero_primary || "Request a Free Consultation"}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/meet-our-dogs"
                data-testid="hero-cta-dogs"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-brand-crimson text-white font-eyebrow text-xs uppercase tracking-[0.2em] font-bold px-7 py-4 rounded-md transition-all duration-300 hover:bg-brand-crimson/10"
              >
                {content?.chrome?.hero_secondary || "Meet the Dogs"}
              </Link>
            </FadeIn>
            <FadeIn delay={1.2} className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-white/60 text-sm">
              {(content?.pages?.home_badges || "Accredited Handlers | 24/7 Rapid Response | UK-Wide Coverage")
                .split("|")
                .map((b, i) => {
                  const Icon = [ShieldCheck, Clock, MapPin][i % 3];
                  return (
                    <span key={i} className="flex items-center gap-2" data-testid={`home-badge-${i}`}>
                      <Icon className="h-4 w-4 text-brand-crimson" /> {b.trim()}
                    </span>
                  );
                })}
            </FadeIn>
          </div>

          <div className="hidden lg:flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, rotate: -6 }}
              animate={{ opacity: 1, scale: 1, rotate: -2 }}
              transition={{ duration: 1.1, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="animate-float-soft relative"
            >
              <div className="absolute -inset-10 rounded-full bg-brand-crimson/25 blur-3xl" />
              <img
                src="/logo.png"
                alt="Quest K9's Detection Dogs crest"
                className="relative w-80 xl:w-[420px] h-auto drop-shadow-[0_30px_60px_rgba(0,0,0,0.65)]"
                data-testid="hero-logo-plaque"
              />
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2"
        >
          <span className="font-eyebrow text-[10px] uppercase tracking-[0.3em] text-white/40">Scroll</span>
          <div className="h-10 w-px bg-gradient-to-b from-brand-crimson to-transparent" />
        </motion.div>
      </section>
    );
  }

  function MarqueeSection({ content }) {
    return <Marquee items={content?.marquee?.items} />;
  }

  function ServicesSection({ content }) {
  const svc = content?.services || {};

  return (
      <section className="py-24 px-4 sm:px-6" data-testid="services-section">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> What We Do</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight max-w-2xl">
              {svc.title || "One team. Every threat covered."}
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {services.map((s, i) => ({ ...s, ...(svc.cards?.[i] || {}) })).map((s, i) => (
              <Reveal key={i} delay={i * 0.08} className={s.span || ""}>
                <div
                  data-testid={`service-card-${i}`}
                  className={`group relative h-full overflow-hidden rounded-lg border border-brand-line bg-brand-surface p-8 transition-all duration-300 hover:border-brand-crimson/70 hover:shadow-[0_0_40px_rgba(200,16,46,0.15)] ${s.span || ""}`}
                >
                  {s.image && (
                    <img
                      src={s.image}
                      alt=""
                      className="absolute inset-y-0 right-0 hidden md:block h-full w-1/2 object-cover opacity-25 group-hover:opacity-40 transition-opacity duration-500 [mask-image:linear-gradient(to_left,black,transparent)]"
                    />
                  )}
                  <div className="relative">
                    <div className="inline-flex items-center justify-center h-12 w-12 rounded-md bg-brand-crimson/15 border border-brand-crimson/30 text-brand-crimson group-hover:bg-brand-crimson group-hover:text-white transition-colors duration-300">
                      <s.icon className="h-6 w-6" />
                    </div>
                    <h3 className="mt-6 text-xl font-semibold text-white">{s.title}</h3>
                    <p className="mt-3 text-sm text-slate-400 leading-relaxed max-w-md">{s.desc}</p>
                    <Link to="/contact-us" className="mt-6 inline-flex items-center gap-1.5 font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold text-brand-crimson hover:text-white transition-colors">
                      Enquire <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function WhySection({ content }) {
  const why = content?.why || {};

  return (
      <section className="py-24 px-4 sm:px-6 bg-brand-surface/40 border-y border-brand-line" data-testid="why-section">
        <div className="mx-auto max-w-7xl grid lg:grid-cols-[1fr_1.3fr] gap-14">
          <div className="lg:sticky lg:top-28 self-start">
            <Reveal>
              <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> Why Dogs</Eyebrow>
              <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">
                {why.title || "The nose knows. Nothing hides from it."}
              </h2>
              <p className="mt-5 text-slate-400 leading-relaxed">
                {why.intro || "Technology helps, but it cannot rival a trained detection dog. Our teams find what scanners miss - faster, calmer and with a lot less fuss for your guests."}
              </p>
              <Link
                to="/about-us"
                data-testid="why-about-link"
                className="mt-7 inline-flex items-center gap-2 font-eyebrow text-[11px] uppercase tracking-[0.2em] font-bold text-brand-crimson hover:text-white transition-colors"
              >
                About Quest K9's <ArrowRight className="h-4 w-4" />
              </Link>
              <div className="mt-8 flex items-center gap-4 rounded-lg border border-brand-line bg-brand-surface p-4 max-w-sm" data-testid="home-knife-accreditation">
                <img src="/knife-detection.png" alt="Knife Detection Dogs accreditation logo" className="h-16 w-auto rounded bg-white p-1.5" />
                <div>
                  <p className="text-sm font-semibold text-white">Knife Detection Dogs</p>
                  <p className="text-xs text-white/50 mt-0.5">Proudly accredited for knife detection work</p>
                </div>
              </div>
            </Reveal>
          </div>
          <div>
            {whyRows.map((r, i) => ({ ...r, ...(why.rows?.[i] || {}) })).map((r, i) => (
              <Reveal key={r.n} delay={i * 0.06}>
                <div className="group flex gap-6 py-7 border-t border-brand-line last:border-b transition-all duration-300 hover:pl-3" data-testid={`why-row-${i}`}>
                  <span className="font-display text-brand-crimson/60 group-hover:text-brand-crimson font-bold text-lg transition-colors">{r.n}</span>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-slate-400 leading-relaxed">{r.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function ProcessSection({ content }) {
  const proc = content?.process || {};

  return (
      <section className="py-24 px-4 sm:px-6" data-testid="process-section">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Eyebrow className="flex items-center gap-3"><span className="h-px w-10 bg-brand-crimson inline-block" /> Deployment</Eyebrow>
            <h2 className="mt-4 font-display font-bold text-2xl sm:text-3xl lg:text-4xl text-white tracking-tight">{proc.title || "From call to sweep report"}</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((p, i) => ({ ...p, ...(proc.steps?.[i] || {}) })).map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div className="relative h-full rounded-lg border border-brand-line bg-brand-surface p-7 hover:border-brand-crimson/60 transition-colors duration-300" data-testid={`process-step-${i}`}>
                  <span className="font-display text-4xl font-extrabold text-brand-crimson/25">{String(i + 1).padStart(2, "0")}</span>
                  <div className="mt-5 inline-flex items-center justify-center h-10 w-10 rounded-md bg-brand-crimson/15 border border-brand-crimson/30 text-brand-crimson">
                    <p.icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    );
  }

  function CtaSection({ content }) {
  const cta = content?.cta || {};
  const contact = content?.contact || {};

  return (
      <section className="py-24 px-4 sm:px-6" data-testid="home-cta-section">
        <div className="mx-auto max-w-4xl text-center">
          <Reveal>
            <Eyebrow>{cta.eyebrow || "Ready When You Are"}</Eyebrow>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white uppercase tracking-tight">
              <AccentLast text={cta.title || "Secure your venue this week"} />
            </h2>
            <p className="mt-5 text-slate-400 max-w-xl mx-auto">
              {cta.text || "Request a sweep, book one of our dogs for your event, or ask us anything - we'll come back with a plan, a team and a fixed price, usually within the hour."}
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a
                href={`tel:${contact.phone || ""}`}
                data-testid="home-cta-phone"
                className="inline-flex items-center gap-2 bg-brand-crimson hover:bg-brand-crimhover text-white font-eyebrow text-xs uppercase tracking-[0.2em] font-bold px-7 py-4 rounded-md transition-all duration-300 hover:shadow-[0_0_32px_rgba(200,16,46,0.55)]"
              >
                <Phone className="h-4 w-4" /> {contact.phone || "Call Us"}
              </a>
              <Link
                to="/contact-us"
                data-testid="home-cta-contact"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-brand-crimson text-white font-eyebrow text-xs uppercase tracking-[0.2em] font-bold px-7 py-4 rounded-md transition-all duration-300 hover:bg-brand-crimson/10"
              >
                {content?.chrome?.cta_message_label || "Send Us a Message"}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

const DEFAULT_ORDER = ["hero", "marquee", "services", "why", "process", "band", "cta"];
const SECTION_COMPONENTS = {
  hero: HeroSection,
  marquee: MarqueeSection,
  services: ServicesSection,
  why: WhySection,
  process: ProcessSection,
  band: ParallaxBand,
  cta: CtaSection,
};

export default function Home() {
  const content = useSiteContent();
  const stored = Array.isArray(content?.section_order) ? content.section_order.filter((id) => SECTION_COMPONENTS[id]) : [];
  const order = [...stored, ...DEFAULT_ORDER.filter((id) => !stored.includes(id))];
  return (
    <main className="bg-brand-obsidian" data-testid="home-page">
      {order.map((id) => {
        const Comp = SECTION_COMPONENTS[id];
        return <Comp key={id} content={content} />;
      })}
    </main>
  );
}
